import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { isConflictError, type ConflictError } from '@eis/insuranceservice-sdk';
import type { QueryKey } from '@tanstack/react-query';

/**
 * A mutation that knows what a conflict is.
 *
 * Every mutating RPC on this platform carries a precondition (decision T-12), so "somebody else
 * changed this while you were editing" is a normal outcome, not an exception. Two rules follow,
 * and this hook exists so no call site has to remember either:
 *
 *  - NEVER retry a conflict. A retry would re-send the write with a fresh ETag, which is a
 *    machine deciding to overwrite the other operator — the exact thing optimistic concurrency
 *    is there to prevent (sdk-generation skill: "never silent retry that overwrites").
 *  - ALWAYS re-read on a conflict. The screen is showing a version the server has moved past, so
 *    the affected queries are invalidated immediately and the user sees the current state under
 *    the standard "data changed, reloaded" message the UI renders from `conflict`.
 *
 * Retrying a transport failure is a different question and stays with the caller's query client.
 */
export interface ServiceMutationOptions<TVariables, TData> {
  readonly mutationFn: (variables: TVariables) => Promise<TData>;
  /**
   * The queries this write affects, derived from the variables — invalidated after a success
   * AND after a conflict, so the screen re-reads either way.
   *
   * A function rather than a list, because the affected resource is named by the variables: a
   * static list would have to be the whole layer, and invalidating a whole layer is how a change
   * to a parent ends up refetching every child (architecture §A.2).
   */
  readonly invalidates: (variables: TVariables) => readonly QueryKey[];
  readonly onSuccess?: (data: TData, variables: TVariables) => void;
}

/**
 * An intersection, not an interface: `UseMutationResult` is a discriminated UNION over idle,
 * pending, error and success, and extending it with an interface would collapse those into one
 * shape — losing exactly the narrowing a caller uses to tell "not started" from "failed".
 */
export type ServiceMutation<TVariables, TData> = UseMutationResult<TData, Error, TVariables> & {
  /** Set when the last attempt lost a race; cleared by the next attempt. */
  readonly conflict: ConflictError | null;
};

export function useServiceMutation<TVariables, TData>({
  mutationFn,
  invalidates,
  onSuccess,
}: ServiceMutationOptions<TVariables, TData>): ServiceMutation<TVariables, TData> {
  const queryClient = useQueryClient();

  const mutation = useMutation<TData, Error, TVariables>({
    mutationFn,
    // A conflict is a decision for the user, never for the client.
    retry: false,
    onSuccess: (data, variables) => {
      for (const key of invalidates(variables))
        void queryClient.invalidateQueries({ queryKey: key });
      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      if (!isConflictError(error)) return;
      // Re-read at once: whatever the screen is showing is provably out of date, and the user
      // has to see the current state before deciding what to do with their edit.
      for (const key of invalidates(variables))
        void queryClient.invalidateQueries({ queryKey: key });
    },
  });

  return {
    ...mutation,
    conflict: isConflictError(mutation.error) ? mutation.error : null,
  };
}
